<script setup>
import { ref, computed } from 'vue';
import SelectCifra from '@/components/inputs/SelectCifra.vue';
import { useEventListener, useLocalStorage } from '@vueuse/core';
import { isEmpty, takeRight } from 'lodash';

const MAX_BOTONES = 5;

const props = defineProps({
  centros: { type: Array, required: true },
  roles: { type: Array, required: true },
});

const conf = useLocalStorage(
  'botones-login-as',
  {
    botones: {},
    ultimoBoton: undefined,
  },
  { mergeDefaults: true },
);

const centroSeleccionado = ref();
const rolSeleccionado = ref();

const rolesActuales = computed(() => {
  if (!centroSeleccionado.value?.id) {
    return [];
  }
  return props.roles.filter((r) => (
    r.centro_id === centroSeleccionado.value.id || r.centro_id === null
  ));
});

const botonDirector = computed(() => {
  if (!centroSeleccionado.value?.id) {
    return null;
  }

  return {
    label: 'Director del centro',
    title: centroSeleccionado.value.nombre,
    esDirector: true,
    iconoDerecha: 'arrow-up',
  };
});

// Aseguramos los datos, que exista la botonera, que no haya botones borrados etc.
function asegurarBotones() {
  const botones = conf.value.botones || {};
  Object.keys(botones).forEach((id) => {
    if (isEmpty(botones[id])) {
      delete botones[id];
    }
  });

  conf.value.botones = botones;
}

// De todos los botones cogemos los 6 más usados
const botones = computed(() => {
  asegurarBotones();
  let botonesOrdenados = Object.values(conf.value.botones).sort(
    (a, b) => (a.numUsos || 0) - (b.numUsos || 0),
  );

  // En la lista de botones no ponemos el último, éste lo pintamos a parte
  const { ultimoBoton } = conf.value;
  if (ultimoBoton) {
    botonesOrdenados = botonesOrdenados.filter((b) => b.idUsuario !== ultimoBoton.idUsuario);
    botonesOrdenados.push(conf.value.ultimoBoton);
  }

  // Cojemos todos los botones menos uno, añadiremos al final el último login
  botonesOrdenados = takeRight(
    botonesOrdenados,
    MAX_BOTONES,
  );

  if (botonDirector.value) {
    botonesOrdenados.unshift({
      ...botonDirector.value,
      idUsuario: `director_${centroSeleccionado.value.id}`,
    });
  }

  return botonesOrdenados;
});

function isUltimoBoton(boton) {
  return boton.idUsuario === conf.value.ultimoBoton?.idUsuario;
}

function classBoton(boton) {
  const ultimo = isUltimoBoton(boton);
  if (boton.esDirector) {
    return {
      'btn-info': true,
    };
  }
  return {
    'btn-success': ultimo,
    'btn-default': !ultimo,
  };
}

function guardarNuevoLogin(loginInfo) {
  let boton = conf.value.botones[loginInfo.usuario_id];
  if (!boton) {
    boton = {
      idUsuario: loginInfo.usuario_id,
      numUsos: 0,
    };
    conf.value.botones[loginInfo.usuario_id] = boton;
  }

  if (loginInfo.usuario_usuario?.startsWith('director_')) {
    boton.label = loginInfo.centro_nombre;
    boton.title = loginInfo.usuario_nombre;
  } else {
    boton.label = loginInfo.usuario_nombre;
    boton.title = loginInfo.centro_nombre;
  }

  boton.numUsos = (boton.numUsos || 0) + 1;
  conf.value.ultimoBoton = boton;
}

function seleccionarBoton(boton) {
  if (boton.numUsos !== undefined) {
    boton.numUsos += 1;
  }
}

function borrarBoton(boton) {
  delete conf.value.botones[boton.idUsuario];
  if (isUltimoBoton(boton)) {
    conf.value.ultimoBoton = null;
  }
}

useEventListener(document.querySelector('.cifra-listado-body'), 'click', (event) => {
  if (event.target.matches('.link-login-as i')) {
    const loginInfo = JSON.parse(event.target.dataset.loginInfo);
    guardarNuevoLogin(loginInfo);
  }
});
</script>

<template>
  <div class="botonera-login-as">
    <div class="row">
      <div class="col-sm-3">
        <div class="form-group">
          <label>{{ $t('Centro') }}</label>
          <SelectCifra
            v-model="centroSeleccionado"
            nombre-input="filtro_admin_usuarios[centro_id]"
            :opciones="centros"
            campo-etiqueta="nombre"
            campo-id="id"
            :permitir-vacio="true"
            :marcador="$t('Todos')"
            :autofoco="true"
          >
          </SelectCifra>
        </div>
      </div>
      <div class="col-sm-3">
        <div class="form-group">
          <label>{{ $t('Rol') }}</label>
          <SelectCifra
            v-model="rolSeleccionado"
            nombre-input="filtro_admin_usuarios[rol_id]"
            :opciones="rolesActuales"
            campo-etiqueta="nombre"
            campo-id="id"
            :permitir-vacio="true"
            :marcador="$t('Todos')"
          >
          </SelectCifra>
        </div>
      </div>
      <div class="col-sm-3">
        <div class="form-group">
          <label>{{ $t('Nombre') }}</label>
          <input
            type="text"
            name="filtro_admin_usuarios[nombre]"
            class="form-control"
          />
        </div>
      </div>
      <div class="col-sm-2">
        <div class="form-group">
          <label>{{ $t('Usuario') }}</label>
          <input
            type="text"
            name="filtro_admin_usuarios[usuario]"
            class="form-control"
          />
        </div>
      </div>
      <div class="col-sm-1">
        <div class="form-group">
          <label>{{ $t('Id') }}</label>
          <input
            type="text"
            name="filtro_admin_usuarios[id]"
            class="form-control"
          />
        </div>
      </div>
    </div>
    <div>
      <div
        v-for="boton in botones"
        :key="boton.idUsuario"
        class="btn-group btn-group-sm m-r-xs"
        role="group"
      >
        <a
          class="btn"
          :class="classBoton(boton)"
          :title="boton.title"
          :usuario_id="boton.idUsuario"
          data-toggle="tooltip"
          :href="`/admin/login_as_do/${boton.idUsuario}`"
          @click="seleccionarBoton(boton)"
        >
          {{ boton.label }}
          <i v-if="boton.iconoDerecha" :class="`fa fa-${boton.iconoDerecha}`"></i>
        </a>
        <button
          v-if="!boton.esDirector"
          type="button"
          class="btn btn-eliminar"
          :class="classBoton(boton)"
          @click="borrarBoton(boton)"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
  .btn {
    letter-spacing: 0;
  }
  .btn-eliminar {
    padding-left: 5px;
    padding-right: 5px;
  }
</style>
<style lang="scss">
  // css global!! Esta ñapa es para ajustar los márgenes arriba y abajo de la botonera
  [data-action="login_as"] {
    .filters-wrapper,
    #botones-accion-listado {
      margin-bottom: 12px;
    }
  }
</style>
